import { createContext, useContext, useEffect, useState, useRef, type ReactNode } from 'react';
import { onAuthStateChanged, signInWithCustomToken, getIdTokenResult, type User } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from './firebase';
import { Shield, Moon, Sun } from 'lucide-react';
import { useDarkMode } from '../hooks/useDarkMode';

export interface LabOSUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  role: string;
  isAdmin: boolean;
}

interface LabOSState {
  user: LabOSUser | null;
  firebaseUser: User | null;
  config: Record<string, unknown>;
  loading: boolean;
  signOut: () => Promise<void>;
}

const Ctx = createContext<LabOSState | null>(null);
const LABOS_URL = import.meta.env.VITE_LABOS_URL;

// Which environment this build was deployed to (dev/prod), so the topbar can
// show it. Set via VITE_ENVIRONMENT in the deploy workflow's .env.production
// (the workflow already knows its target GitHub Environment name). Local
// runs don't go through that workflow, so fall back to Vite's own dev/build
// mode instead.
const ENVIRONMENT = (import.meta.env.VITE_ENVIRONMENT || (import.meta.env.DEV ? 'dev' : 'prod')).toLowerCase();

// LabOS app registration this dashboard authenticates against. Falls back to the dev
// registration so local development works without the variable set.
export const LABOS_APP_ID = import.meta.env.VITE_LABOS_APP_ID || 'comfyui-manager-wbd';

export function LabOSAuth({ children, appId, fallback }: { children: ReactNode; appId: string; fallback?: ReactNode }) {
  const [state, setState] = useState<Omit<LabOSState, 'signOut'>>({
    user: null,
    firebaseUser: null,
    config: {},
    loading: true,
  });
  const isHandshakeCodeProccessing = useRef(false);

  useEffect(() => {
    // Check for handshake code in URL (redirect from LabOS)
    const params = new URLSearchParams(window.location.search);
    const handshakeCode = params.get('handshake');

    if (handshakeCode) {
      // Redeem the handshake code for a Firebase custom token
      const redeem = httpsCallable<{ code: string }, { customToken: string }>(functions, 'redeemHandshakeCode');
      isHandshakeCodeProccessing.current = true;
      redeem({ code: handshakeCode })
        .then(async (result) => {
          await signInWithCustomToken(auth, result.data.customToken);
        })
        .catch((err) => {
          console.error('[LabOS] Handshake redemption failed:', err);
        })
        .finally(() => {
          isHandshakeCodeProccessing.current = false;
          // Clean up URL
          params.delete('handshake');
          params.delete('origin');
          const clean = window.location.pathname + (params.toString() ? '?' + params : '');
          window.location.href = clean;
        });
    }

    return onAuthStateChanged(auth, async (fbUser) => {
      if (isHandshakeCodeProccessing.current) return;
      if (!fbUser) {
        // Not authenticated — redirect to LabOS
        const returnUrl = encodeURIComponent(window.location.href);
        window.location.href = `${LABOS_URL}/login?return=${returnUrl}&app=${appId}`;
        return;
      }

      const token = await getIdTokenResult(fbUser);
      const role = (token.claims.role as string) || 'user';

      // Fetch runtime config from LabOS (group-level app parameters)
      let config: Record<string, unknown> = {};
      try {
        const getAppConfig = httpsCallable<{ appId: string }, Record<string, unknown>>(functions, 'getAppConfig');
        const result = await getAppConfig({ appId });
        config = result.data || {};
      } catch {
        /* config is optional */
      }

      setState({
        user: {
          uid: fbUser.uid,
          email: fbUser.email,
          displayName: fbUser.displayName,
          role,
          isAdmin: role === 'admin',
        },
        firebaseUser: fbUser,
        config,
        loading: false,
      });
    });
  }, [appId]);

  const signOut = async () => {
    await auth.signOut();
    window.location.href = LABOS_URL;
  };

  if (state.loading) {
    // Check localStorage for dark mode preference
    const isDark = localStorage.getItem('darkMode') === 'true';

    return (
      <>
        {fallback || (
          <div
            style={{
              minHeight: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: isDark ? '#141414' : '#fff',
              color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.3)',
              fontSize: '14px',
              fontFamily: 'system-ui',
            }}
          >
            Authenticating...
          </div>
        )}
      </>
    );
  }

  return <Ctx.Provider value={{ ...state, signOut }}>{children}</Ctx.Provider>;
}

// Simple mock for development - provides context without errors
export function LabOSAuthMock({ children }: { children: ReactNode }) {
  const mockState: LabOSState = {
    user: null,
    firebaseUser: null,
    config: {},
    loading: false,
    signOut: async () => {
      console.log('[LabOS Mock] Sign out called');
    },
  };

  return <Ctx.Provider value={mockState}>{children}</Ctx.Provider>;
}

export function useLabOS(): LabOSState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useLabOS() must be inside <LabOSAuth>');
  return ctx;
}

export function LabOSTopbar({ appName }: { appName: string; accentColor?: string }) {
  const { user, signOut } = useLabOS();
  const { isDark, toggleDarkMode } = useDarkMode();

  return (
    <div
      className="transition-colors duration-200 backdrop-blur-sm"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '48px',
        padding: '0 24px',
        background: isDark ? 'rgba(20, 20, 20, 0.95)' : 'rgba(255, 255, 255, 0.95)',
        borderBottom: isDark ? '1px solid rgba(102, 126, 234, 0.15)' : '1px solid rgba(0,0,0,0.06)',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        fontSize: '13px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <a
          href={LABOS_URL}
          style={{
            color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)',
            textDecoration: 'none',
            fontWeight: 600,
            letterSpacing: '0.03em',
            fontSize: '13px',
            transition: 'color 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = isDark ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.6)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)';
          }}
        >
          LabOS
        </a>
        <span style={{ color: isDark ? 'rgba(102, 126, 234, 0.3)' : 'rgba(0,0,0,0.15)', fontWeight: 300 }}>/</span>
        <span
          style={{
            background: isDark
              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 700,
            fontSize: '14px',
            letterSpacing: '-0.01em',
          }}
        >
          {appName}
        </span>
      </div>
      <span
        style={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '2px 8px',
          borderRadius: '4px',
          fontWeight: 600,
          fontSize: '10px',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          color: ENVIRONMENT === 'prod' ? '#b91c1c' : '#92400e',
          background: ENVIRONMENT === 'prod' ? 'rgba(239,68,68,0.1)' : 'rgba(245,158,11,0.12)',
          border: `1px solid ${ENVIRONMENT === 'prod' ? 'rgba(239,68,68,0.3)' : 'rgba(245,158,11,0.3)'}`,
        }}
      >
        {ENVIRONMENT}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <a
          href="/"
          style={{
            color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)',
            textDecoration: 'none',
            fontSize: '11px',
            fontWeight: 500,
          }}
        >
          Home
        </a>
        {user?.isAdmin && (
          <a
            href="/admin"
            style={{
              color: 'rgba(217, 119, 6, 0.8)',
              textDecoration: 'none',
              fontSize: '11px',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              borderRadius: '8px',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = 'rgba(180, 83, 9, 1)';
              e.currentTarget.style.backgroundColor = isDark ? 'rgba(254, 243, 199, 0.15)' : 'rgba(254, 243, 199, 1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = 'rgba(217, 119, 6, 0.8)';
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <Shield style={{ width: '14px', height: '14px' }} />
            Admin
          </a>
        )}
        <button
          onClick={toggleDarkMode}
          title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          style={{
            background: 'none',
            border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)',
            color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.4)',
            padding: '4px 8px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '11px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'none';
          }}
        >
          {isDark ? <Sun style={{ width: '14px', height: '14px' }} /> : <Moon style={{ width: '14px', height: '14px' }} />}
        </button>
        <span style={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.3)' }}>{user?.email}</span>
        <button
          onClick={signOut}
          style={{
            background: 'none',
            border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)',
            color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)',
            padding: '2px 8px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '11px',
          }}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
